/**
 * Palermo House analytics facade — dual Mixpanel + GrowthBook Managed Warehouse sinks.
 * Requires window.PALERMO_ANALYTICS from snippets/analytics-bootstrap.liquid.
 *
 * Product/funnel events use the same names in both sinks.
 * Experiment exposure: Mixpanel `$experiment_started`; GB auto `Experiment Viewed` via data-tracking.
 */
(function () {
  'use strict';

  var cfg = window.PALERMO_ANALYTICS;
  if (!cfg || !cfg.enabled) return;

  var mixpanelEnabled = !!(cfg.mixpanelEnabled && cfg.mixpanelToken);
  var growthbookEnabled = !!(cfg.growthbookEnabled && cfg.growthbookClientKey);
  if (!mixpanelEnabled && !growthbookEnabled) return;

  var CART_ATTR_DISTINCT = 'mp_distinct_id';
  var CART_ATTR_EXPS = 'gb_exps';
  var RETIRED_EXPERIMENT_KEYS = { 'aa-pipeline-check': true, 'palermo-sale-filters-v1': true };
  var experimentMap = {};
  var exposedExperiments = {};
  var initialized = false;
  var pageViewSent = false;
  var mixpanelInstance = null;
  var experimentUnitId = null;

  window.gbEvents = window.gbEvents || [];

  /* Events fired before start() used to be dropped: Mixpanel's CDN script
     loads async, so early component events hit a no-op stub. Buffer instead
     and flush in start() after the GrowthBook exposure pass. Capped: a
     consent-blocked page never calls start() and must not grow it unbounded. */
  var pendingEvents = [];
  var PENDING_LIMIT = 50;

  window.PalermoAnalytics = window.PalermoAnalytics || {
    track: function (eventName, props) {
      if (pendingEvents.length < PENDING_LIMIT) {
        pendingEvents.push([eventName, props]);
      }
    },
    identify: function () {},
    syncCartAttributes: function () { return syncCartAttributes(); },
    ready: false
  };

  function marketingAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') {
        return true;
      }
      return privacy.analyticsProcessingAllowed();
    } catch (e) {
      return true;
    }
  }

  function sharedProps(props) {
    return Object.assign(
      {
        currency: cfg.currency,
        template_name: cfg.template && cfg.template.name,
        page_type: cfg.template && cfg.template.pageType,
        path: cfg.path || (window.location && window.location.pathname)
      },
      props || {}
    );
  }

  /* ---------- Mixpanel sink ---------- */

  function finishMixpanelInit(callback) {
    if (!window.mixpanel || typeof window.mixpanel.init !== 'function') {
      console.warn('[PalermoAnalytics] Mixpanel loaded without init');
      return;
    }
    window.mixpanel.init(cfg.mixpanelToken, {
      batch_requests: true,
      persistence: 'localStorage',
      loaded: function (mx) {
        mx.__loaded = true;
        callback(mx);
      }
    });
  }

  function loadMixpanel(callback) {
    if (window.mixpanel && typeof window.mixpanel.track === 'function' && window.mixpanel.__loaded) {
      callback(window.mixpanel);
      return;
    }

    if (window.mixpanel && !window.mixpanel.__SV) {
      var hasRealInit = typeof window.mixpanel.init === 'function' && !Array.isArray(window.mixpanel);
      if (hasRealInit) {
        finishMixpanelInit(callback);
        return;
      }
      try {
        delete window.mixpanel;
      } catch (e) {
        window.mixpanel = undefined;
      }
    }

    if (!window.mixpanel || !window.mixpanel.__SV) {
      (function (f, b) {
        if (b.__SV) return;
        var e, g, i, h;
        window.mixpanel = b;
        b._i = [];
        b.init = function (e, f, c) {
          function g(a, d) {
            var parts = d.split('.');
            if (parts.length === 2) {
              a = a[parts[0]];
              d = parts[1];
            }
            a[d] = function () {
              a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
            };
          }
          var a = b;
          if (typeof c !== 'undefined') {
            a = b[c] = [];
          } else {
            c = 'mixpanel';
          }
          a.people = a.people || [];
          a.toString = function (skipStub) {
            var name = 'mixpanel';
            if (c !== 'mixpanel') name += '.' + c;
            if (!skipStub) name += ' (stub)';
            return name;
          };
          a.people.toString = function () {
            return a.toString(1) + '.people (stub)';
          };
          i =
            'disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove'.split(
              ' '
            );
          for (h = 0; h < i.length; h++) g(a, i[h]);
          var groupMethods = 'set set_once union unset remove delete'.split(' ');
          a.get_group = function () {
            function bindMethod(methodName) {
              groupApi[methodName] = function () {
                var call2 = [methodName].concat(Array.prototype.slice.call(arguments, 0));
                a.push([groupArgs, call2]);
              };
            }
            var groupApi = {};
            var groupArgs = ['get_group'].concat(Array.prototype.slice.call(arguments, 0));
            for (var gi = 0; gi < groupMethods.length; gi++) bindMethod(groupMethods[gi]);
            return groupApi;
          };
          b._i.push([e, f, c]);
        };
        b.__SV = 1.2;
        e = f.createElement('script');
        e.type = 'text/javascript';
        e.async = true;
        e.src = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
        e.onerror = function () {
          /* cdn.mxpnl.com is on ad-block lists. Start without Mixpanel so
             identity and experiment cart attributes still reach those
             visitors' orders. */
          console.warn('[PalermoAnalytics] Mixpanel failed to load — starting without it');
          callback(null);
        };
        g = f.getElementsByTagName('script')[0];
        g.parentNode.insertBefore(e, g);
      })(document, window.mixpanel || []);
    }

    finishMixpanelInit(callback);
  }

  var MixpanelSink = {
    enabled: mixpanelEnabled,
    track: function (eventName, props) {
      if (!mixpanelEnabled || !mixpanelInstance || typeof mixpanelInstance.track !== 'function') return;
      try {
        mixpanelInstance.track(eventName, props);
      } catch (e) {
        /* ignore */
      }
    },
    identify: function (id, traits) {
      if (!mixpanelEnabled || !mixpanelInstance) return;
      try {
        mixpanelInstance.identify(id);
        if (traits) mixpanelInstance.people.set(traits);
      } catch (e) {
        /* ignore */
      }
    },
    register: function (props) {
      if (!mixpanelEnabled || !mixpanelInstance) return;
      try {
        mixpanelInstance.register(props);
      } catch (e) {
        /* ignore */
      }
    },
    getDistinctId: function () {
      if (!mixpanelEnabled || !mixpanelInstance) return null;
      try {
        return mixpanelInstance.get_distinct_id();
      } catch (e) {
        return null;
      }
    }
  };

  /* ---------- GrowthBook Managed Warehouse sink ---------- */

  var GrowthBookSink = {
    enabled: growthbookEnabled,
    track: function (eventName, props) {
      if (!growthbookEnabled) return;
      // Skip Mixpanel-only exposure event; GB gets Experiment Viewed via data-tracking
      if (eventName === '$experiment_started') return;
      try {
        window.gbEvents = window.gbEvents || [];
        window.gbEvents.push({
          eventName: eventName,
          properties: props || {}
        });
      } catch (e) {
        /* ignore */
      }
    }
  };

  /* ---------- Facade ---------- */

  function track(eventName, props) {
    if (!initialized) return;
    var payload = sharedProps(props);
    MixpanelSink.track(eventName, payload);
    GrowthBookSink.track(eventName, payload);
  }

  function configuredExperimentUnitId() {
    var growthbookConfig = window.growthbook_config || {};
    var attributes = growthbookConfig.attributes || {};
    return (
      window.__palermoExperimentUnitId ||
      growthbookConfig.uuid ||
      attributes.device_id ||
      attributes.id ||
      null
    );
  }

  function getExperimentUnitId() {
    if (experimentUnitId) return experimentUnitId;

    /* Capture once, before customer identification can change Mixpanel's
       distinct id. The inline bootstrap normally supplies this synchronously;
       the remaining paths only keep analytics fail-safe if that bootstrap
       could not persist or expose its value. */
    experimentUnitId = configuredExperimentUnitId() || MixpanelSink.getDistinctId();
    if (!experimentUnitId) {
      experimentUnitId =
        'anon_' +
        Math.random().toString(36).slice(2) +
        Date.now().toString(36);
    }
    return String(experimentUnitId);
  }

  function getDistinctId() {
    return getExperimentUnitId();
  }

  function identifyCustomer() {
    if (!cfg.customer || !cfg.customer.id) return;
    var distinct = 'customer_' + cfg.customer.id;
    MixpanelSink.identify(distinct, {
      $email: cfg.customer.email || undefined,
      shopify_customer_id: String(cfg.customer.id)
    });
  }

  /* Assignments persisted at decision time by the bootstrap's
     __palermoPersistExposure (localStorage, 30-day entries). */
  function readPersistedExperiments() {
    try {
      var raw = window.localStorage.getItem('palermo_gb_exps');
      var parsed = raw ? JSON.parse(raw) : {};
      if (!parsed || typeof parsed !== 'object') return {};
      var now = Date.now();
      var out = {};
      var kept = {};
      for (var key in parsed) {
        var entry = parsed[key];
        if (
          !RETIRED_EXPERIMENT_KEYS[key] &&
          entry && typeof entry === 'object' &&
          typeof entry.v === 'string' &&
          now - (entry.t || 0) < 30 * 24 * 60 * 60 * 1000
        ) {
          out[key] = entry.v;
          kept[key] = entry;
        }
      }
      window.localStorage.setItem('palermo_gb_exps', JSON.stringify(kept));
      return out;
    } catch (e) {
      return {};
    }
  }

  function syncCartAttributes() {
    var distinctId = getExperimentUnitId();
    if (!distinctId) return;

    function activeExperiments(cartExperiments) {
      var merged = Object.assign({}, cartExperiments || {}, readPersistedExperiments(), experimentMap);
      for (var key in RETIRED_EXPERIMENT_KEYS) {
        delete merged[key];
      }
      return merged;
    }

    function writeCart(experiments, allowEmpty) {
      var attrs = {};
      attrs[CART_ATTR_DISTINCT] = String(distinctId);
      if (Object.keys(experiments).length > 0) {
        attrs[CART_ATTR_EXPS] = JSON.stringify(experiments);
      } else if (allowEmpty) {
        /* Empty string removes the retired assignment without touching other
           cart attributes. We only allow this after reading the current cart. */
        attrs[CART_ATTR_EXPS] = '';
      }
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        credentials: 'same-origin',
        keepalive: true
      }).catch(function () {
        /* non-blocking */
      });
    }

    /* Read-modify-write preserves homepage and PDP assignments while removing
       only retired experiment keys from carts created before shutdown. */
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (response) {
      if (!response || !response.ok) throw new Error('cart read failed');
      return response.json();
    }).then(function (cart) {
      var cartExperiments = {};
      try {
        var raw = cart && cart.attributes && cart.attributes[CART_ATTR_EXPS];
        var parsed = raw ? JSON.parse(raw) : {};
        if (parsed && typeof parsed === 'object') cartExperiments = parsed;
      } catch (e) {
        /* malformed cart assignment — replace it with known active keys */
      }
      return writeCart(activeExperiments(cartExperiments), true);
    }).catch(function () {
      /* A failed cart read must never erase assignments from other tests. */
      return writeCart(activeExperiments({}), false);
    });
  }

  function recordExperimentExposure(experimentKey, variation) {
    if (!experimentKey || variation == null || String(variation) === '') return;
    var key = String(experimentKey);
    if (RETIRED_EXPERIMENT_KEYS[key]) return;
    var variant = String(variation);
    experimentMap[key] = variant;
    /* wireGrowthBook replaces the bootstrap trackingCallback, so exposures
       recorded after boot must persist here to stay navigation-proof. */
    if (typeof window.__palermoPersistExposure === 'function') {
      window.__palermoPersistExposure(key, variant);
    }

    if (!exposedExperiments[key]) {
      exposedExperiments[key] = true;
      MixpanelSink.track(
        '$experiment_started',
        sharedProps({
          'Experiment name': key,
          'Variant name': variant,
          $source: 'growthbook'
        })
      );
      var superProps = {};
      superProps['exp_' + key] = variant;
      MixpanelSink.register(superProps);
    }

    syncCartAttributes();
  }

  function sanitizeFeatureKey(key) {
    return String(key || '')
      .trim()
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function collectGbFeatureKeys() {
    var keys = {};
    var nodes = document.querySelectorAll('[data-gb-feature]');
    for (var i = 0; i < nodes.length; i++) {
      var raw = nodes[i].getAttribute('data-gb-feature');
      if (raw && !RETIRED_EXPERIMENT_KEYS[raw]) keys[raw] = true;
    }
    var extras = window.PALERMO_GB_FEATURES;
    if (Array.isArray(extras)) {
      for (var j = 0; j < extras.length; j++) {
        if (extras[j] && !RETIRED_EXPERIMENT_KEYS[extras[j]]) keys[extras[j]] = true;
      }
    }
    return Object.keys(keys);
  }

  function clearFeatureClasses(root, safeKey) {
    var prefix = 'e-' + safeKey + '-';
    var toRemove = [];
    for (var i = 0; i < root.classList.length; i++) {
      var cls = root.classList[i];
      if (cls.indexOf(prefix) === 0) toRemove.push(cls);
    }
    for (var j = 0; j < toRemove.length; j++) {
      root.classList.remove(toRemove[j]);
    }
  }

  function variationKeyFromEval(result) {
    if (!result) return null;
    var er = result.experimentResult;
    if (er && er.key != null && String(er.key) !== '') return String(er.key);
    if (er && er.variationId != null) return String(er.variationId);
    return null;
  }

  function applyGbExperimentClasses(gb) {
    if (!gb || !document.documentElement) return;
    var root = document.documentElement;
    var features = collectGbFeatureKeys();
    var assignment = {};

    for (var i = 0; i < features.length; i++) {
      var featureKey = features[i];
      var safeKey = sanitizeFeatureKey(featureKey);
      if (!safeKey) continue;

      clearFeatureClasses(root, safeKey);

      var on = false;
      var variation = null;
      try {
        if (typeof gb.evalFeature === 'function') {
          var evaluated = gb.evalFeature(featureKey);
          on = !!(evaluated && evaluated.on);
          variation = variationKeyFromEval(evaluated);
          var er = evaluated && evaluated.experimentResult;
          var exp = evaluated && evaluated.experiment;
          /* Record an exposure only when the SDK hashed (or sticky-bucketed) the
             visitor into the arm, the same rule its own trackingCallback uses. A
             forced arm (query-string override, forced variation) is shown but
             is not an assignment, so it never reaches cart attributes or Mixpanel. */
          if (er && er.inExperiment && er.hashUsed === true && exp && exp.key) {
            var expVariation = null;
            if (er.key != null && String(er.key) !== '') {
              expVariation = String(er.key);
            } else if (er.variationId != null) {
              expVariation = String(er.variationId);
            }
            if (expVariation != null) {
              recordExperimentExposure(exp.key, expVariation);
            }
          }
        } else if (typeof gb.isOn === 'function') {
          on = !!gb.isOn(featureKey);
        }
      } catch (e) {
        on = false;
      }

      root.classList.add(on ? 'e-' + safeKey + '-on' : 'e-' + safeKey + '-off');

      if (variation) {
        var safeVariation = sanitizeFeatureKey(variation);
        if (safeVariation) {
          root.classList.add('e-' + safeKey + '-' + safeVariation);
          assignment[featureKey] = { on: on, showToken: safeVariation, safeKey: safeKey };
        } else {
          assignment[featureKey] = { on: on, showToken: on ? 'on' : 'off', safeKey: safeKey };
        }
      } else {
        assignment[featureKey] = { on: on, showToken: on ? 'on' : 'off', safeKey: safeKey };
      }
    }

    var nodes = document.querySelectorAll('[data-gb-feature][data-gb-show]');
    for (var n = 0; n < nodes.length; n++) {
      var el = nodes[n];
      var key = el.getAttribute('data-gb-feature');
      var want = sanitizeFeatureKey(el.getAttribute('data-gb-show') || '');
      var state = assignment[key];
      var visible = false;
      if (state && want) {
        if (want === 'on') visible = !!state.on;
        else if (want === 'off') visible = !state.on;
        else visible = state.showToken === want;
      }
      el.setAttribute('data-gb-visible', visible ? 'true' : 'false');
    }

    root.classList.add('gb-experiments-ready');
  }

  function wireGrowthBook() {
    var distinctId = getExperimentUnitId();

    function onExperiment(experiment, result) {
      if (!experiment || !result) return;
      var variation =
        result.key != null ? String(result.key) : String(result.variationId);
      recordExperimentExposure(experiment.key, variation);
    }

    window.growthbook_config = window.growthbook_config || {};
    window.growthbook_config.uuid = distinctId;
    window.growthbook_config.trackingCallback = onExperiment;

    var queued = window.__gbExposureQueue || [];
    window.__gbExposureQueue = [];
    for (var i = 0; i < queued.length; i++) {
      try {
        onExperiment(queued[i][0], queued[i][1]);
      } catch (e) {
        /* ignore */
      }
    }

    if (!growthbookEnabled) return onExperiment;

    window.growthbook_queue = window.growthbook_queue || [];
    window.growthbook_queue.push(function (gb) {
      var attrs = Object.assign({}, (gb.getAttributes && gb.getAttributes()) || {}, {
        id: distinctId,
        device_id: distinctId,
        path: cfg.path || (window.location && window.location.pathname) || ''
      });
      if (cfg.customer && cfg.customer.id) {
        attrs.customerId = String(cfg.customer.id);
        attrs.user_id = 'customer_' + cfg.customer.id;
      }
      if (gb.setAttributes) gb.setAttributes(attrs);

      function apply() {
        applyGbExperimentClasses(gb);
      }
      apply();
      document.addEventListener('growthbookdata', apply);
    });

    return onExperiment;
  }

  function emitPageView() {
    if (pageViewSent) return;
    pageViewSent = true;
    track('page_view', {
      title: document.title,
      url: window.location.href,
      referrer: document.referrer || undefined
    });
  }

  function emitProductViewed() {
    if (!cfg.product) return;
    track('product_viewed', {
      product_id: cfg.product.id,
      product_handle: cfg.product.handle,
      product_title: cfg.product.title,
      product_vendor: cfg.product.vendor,
      product_type: cfg.product.type,
      variant_id: cfg.product.variantId,
      variant_title: cfg.product.variantTitle,
      price: cfg.product.price != null ? cfg.product.price / 100 : undefined
    });
  }

  function wireCtaClicks() {
    document.addEventListener(
      'click',
      function (event) {
        var el = event.target && event.target.closest
          ? event.target.closest('[data-track-cta]')
          : null;
        if (!el) return;
        track('cta_clicked', {
          cta_name: el.getAttribute('data-track-cta') || el.textContent.trim().slice(0, 80),
          location: el.getAttribute('data-track-location') || undefined,
          href: el.getAttribute('href') || undefined
        });
      },
      true
    );
  }

  function parseCartAddPayload(input) {
    if (!input) return {};
    if (typeof input === 'string') {
      try {
        return JSON.parse(input);
      } catch (e) {
        return {};
      }
    }
    if (input instanceof FormData) {
      var obj = {};
      input.forEach(function (v, k) {
        obj[k] = v;
      });
      return obj;
    }
    return input;
  }

  function trackAddToCartFromBody(body) {
    var data = parseCartAddPayload(body);
    var items = data.items;
    if (Array.isArray(items) && items.length) {
      items.forEach(function (item) {
        track('product_added_to_cart', {
          product_id: item.product_id,
          variant_id: item.id || item.variant_id,
          quantity: item.quantity || 1,
          selling_plan: item.selling_plan || undefined,
          value: item.price != null ? Number(item.price) / 100 : undefined
        });
      });
      return;
    }

    track('product_added_to_cart', {
      variant_id: data.id || data.variant_id,
      quantity: data.quantity || 1,
      selling_plan: data.selling_plan || undefined
    });
  }

  function wireCartAddHooks() {
    if (window.fetch) {
      var originalFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : input && input.url;
        var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        var isCartAdd =
          url &&
          method === 'POST' &&
          (/\/cart\/add(\.js)?(\?|$)/.test(url) || String(url).indexOf('/cart/add') !== -1);

        var promise = originalFetch.apply(this, arguments);
        if (!isCartAdd) return promise;

        var body = init && init.body;
        return promise.then(function (response) {
          if (response && response.ok) {
            try {
              trackAddToCartFromBody(body);
            } catch (e) {
              /* ignore */
            }
          }
          return response;
        });
      };
    }

    if (window.XMLHttpRequest) {
      var originalOpen = XMLHttpRequest.prototype.open;
      var originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url) {
        this.__palermoMethod = method;
        this.__palermoUrl = url;
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function (body) {
        var xhr = this;
        var method = (xhr.__palermoMethod || '').toUpperCase();
        var url = String(xhr.__palermoUrl || '');
        var isCartAdd = method === 'POST' && url.indexOf('/cart/add') !== -1;

        if (isCartAdd) {
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                trackAddToCartFromBody(body);
              } catch (e) {
                /* ignore */
              }
            }
          });
        }
        return originalSend.apply(this, arguments);
      };
    }
  }

  function clearRetiredMixpanelAssignments(mx) {
    if (!mx || typeof mx.unregister !== 'function') return;
    for (var key in RETIRED_EXPERIMENT_KEYS) mx.unregister('exp_' + key);
  }

  function start(mx) {
    clearRetiredMixpanelAssignments(mx);
    if (initialized) {
      /* Mixpanel finishing after the no-Mixpanel fallback already started
         us: adopt the instance so the session's later events still flow. */
      if (mx && !mixpanelInstance) mixpanelInstance = mx;
      return;
    }
    initialized = true;
    mixpanelInstance = mx || null;

    /* Freeze the experiment unit before Mixpanel identify can switch its
       distinct id to customer_*. Assignment and cart/order joins stay fixed. */
    getExperimentUnitId();
    var onExperiment = wireGrowthBook();
    syncCartAttributes();
    identifyCustomer();

    window.PalermoAnalytics = {
      ready: true,
      track: track,
      onExperiment: onExperiment,
      identify: function (id, traits) {
        MixpanelSink.identify(id, traits);
      },
      syncCartAttributes: syncCartAttributes,
      getDistinctId: getDistinctId,
      sinks: {
        mixpanel: mixpanelEnabled,
        growthbook: growthbookEnabled
      }
    };

    emitPageView();
    emitProductViewed();
    wireCtaClicks();
    // wireSellingPlanSelection(); // deactivated — event volume
    wireCartAddHooks();

    /* Anything emitted before we were ready, in the order it happened. */
    var buffered = pendingEvents.splice(0, pendingEvents.length);
    for (var i = 0; i < buffered.length; i++) {
      track(buffered[i][0], buffered[i][1]);
    }
  }

  function boot() {
    function run() {
      if (mixpanelEnabled) {
        loadMixpanel(start);
        /* Watchdog: some blockers stall the Mixpanel script without firing
           onerror. Identity and cart-attribute syncing must not wait on a
           third-party CDN — start() is idempotent and adopts Mixpanel
           later if it does finish loading. */
        setTimeout(function () {
          start(null);
        }, 4000);
      } else {
        start(null);
      }
    }

    if (!marketingAllowed()) {
      document.addEventListener('visitorConsentCollected', function () {
        if (marketingAllowed()) run();
      });
      return;
    }
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
